import React, { useState, useEffect, useCallback, useMemo } from "react";
import LedgerService from "../services/LedgerService";
import LedgerModal from "../components/LedgerModal";
import LedgerViewModal from "../components/LedgerViewModal";
import ConfirmationModal from "../components/ConfirmationModal";
import LedgerListing from "../components/LedgerListing"; // import the new listing component
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrash, faEye } from "@fortawesome/free-solid-svg-icons";
import { useLoading } from "../context/LoadingContext";
import { showSuccessToast, showErrorToast } from "../utils/AlertUtils";
import guLocale from "../locales/gu";

const Ledger = () => {
  const locale = guLocale.confirmationModal;
  const ledgerListingLocale = guLocale.ledgerListing;
  const { setIsLoading } = useLoading();
  const [filterText, setFilterText] = useState("");
  const [ledgers, setLedgers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [currentLedger, setCurrentLedger] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [ledgerIdToDelete, setLedgerIdToDelete] = useState(null);
  const [totalGrant, setTotalGrant] = useState(0);
  const [usedGrant, setUsedGrant] = useState(0);
  const [remainingGrant, setRemainingGrant] = useState(0);

  const getPaymentMethodText = (method) => {
    switch (method) {
      case "cash":
        return ledgerListingLocale.paymentMethodCash;
      case "online":
        return ledgerListingLocale.paymentMethodOnline;
      case "cheque":
        return ledgerListingLocale.paymentMethodCheque;
      default:
        return method;
    }
  };

  const columns = [
    {
      name: ledgerListingLocale.accountDetails, // Changed name to "Details" to reflect combined info
      cell: (row) => (
        <div className="py-2">
          <div className="text-xs text-gray-500">
          {new Date(row.transaction_date).toLocaleDateString('en-GB')}
            <span className="badge badge-primary text-xs ml-1">
              { getPaymentMethodText(row.payment_method) }
            </span>
          </div>
          <div className="text-bold">{row?.head_id?.head_name}</div>
        </div>
      ),
      sortable: true,
      width: "350px",
    },
    {
      name: ledgerListingLocale.details,
      selector: (row) => row.details,
      sortable: true,
      width: "300px",
    },
    {
      name: ledgerListingLocale.amount,
      cell: (row) => {
        // Add cell function
        const type = row.transaction_type;
        const amount = row.amount;
        if (type === "IN") {
          return <span className="text-bold text-success">₹{amount}</span>;
        } else {
          return <span className="text-bold text-danger">₹{amount}</span>;
        }
      },
      sortable: true,
      width: "150px",
    },
    {
      name: ledgerListingLocale.type,
      cell: (row) => {
        // Add cell function
        const type = row.transaction_type;
        if (type === "OUT") {
          return <span className="badge badge-danger text-xs">{ledgerListingLocale.out}</span>;
        } else if (type === "IN") {
          return <span className="badge badge-success text-xs">{ledgerListingLocale.in}</span>;
        } else {
          return <span>{type}</span>; // Default case, in case of other types
        }
      },
      sortable: true,
      width: "150px",
    },
    {
      name: ledgerListingLocale.action,
      cell: (row) => (
        <>
          <button
            className="btn btn-info btn-sm mr-2" // Added info button for View
            onClick={() => handleView(row)} // handleView function
          >
            <FontAwesomeIcon icon={faEye} />
          </button>
          <button
            className="btn btn-primary btn-sm mr-2"
            onClick={() => handleEdit(row)}
          >
            <FontAwesomeIcon icon={faEdit} />
          </button>
          <button
            className="btn btn-danger btn-sm"
            onClick={() => handleDeleteClick(row._id)}
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </>
      ),
      width: "120px",
    },
  ];

  const fetchLedgers = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await LedgerService.getLedgers();
      if(response && response.statusCode === 200) {
        setLedgers(response.data);
      }
    } catch (error) {
      console.error("Error fetching ledgers", error);
      showErrorToast(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading]);

  const fetchLedgerCalculations = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await LedgerService.ledgerCalculations();
      if(response && response.statusCode === 200){
        setTotalGrant(response?.data?.totalGrant);
        setUsedGrant(response?.data?.usedGrant);
        setRemainingGrant(response?.data?.remainingGrant);
      }
    } catch (error) {
      console.error("Error fetching ledgers", error);
      showErrorToast(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading]);

  useEffect(() => {
    fetchLedgers();
    fetchLedgerCalculations();
  }, [fetchLedgers, fetchLedgerCalculations]);

  const filteredData = useMemo(() => {
    const lowerFilterText = filterText.toLowerCase();
    return ledgers.filter((item) => {
      // Check for matches in "Transaction Details" column (Date, Payment Method, Head Name)
      const transactionDetails = `${new Date(
        item.transaction_date
      ).toLocaleDateString()} ${item.payment_method} ${
        item.head_id?.head_name
      }`.toLowerCase();
      const detailsMatch = transactionDetails.includes(lowerFilterText);

      // Check for matches in "Description" column
      const descriptionMatch = item.details
        ?.toLowerCase()
        .includes(lowerFilterText);

      // Check for matches in "Amount" column (optional, as it's rendered in cell) - you might need to adjust based on your needs
      const amountMatch = item.amount
        ?.toString()
        .toLowerCase()
        .includes(lowerFilterText);

      // Check for matches in "Transaction Type" column (optional, as it's rendered in cell) - you might need to adjust based on your needs
      const transactionTypeMatch = item.transaction_type
        ?.toLowerCase()
        .includes(lowerFilterText);

      return (
        detailsMatch || descriptionMatch || amountMatch || transactionTypeMatch
      );
    });
  }, [ledgers, filterText]);

  const handleFilterChange = useCallback((e) => {
    setFilterText(e.target.value); // Only update filterText state
  }, []);

  const handleAdd = () => {
    setCurrentLedger(null);
    setShowModal(true);
  };

  const handleEdit = (ledger) => {
    setCurrentLedger(ledger);
    setShowModal(true);
  };

  const handleView = async (ledger) => {
    // handleView function
    setIsLoading(true);
    try {
      const response = await LedgerService.getLedgerById(ledger._id); // API call to get ledger by ID
      if (response && response.statusCode === 200) {
        setCurrentLedger(response.data); // Set the current ledger with fetched data
        setShowViewModal(true); // Show the modal to view details
      } else {
        showErrorToast("Failed to fetch ledger details.");
      }
    } catch (error) {
      console.error("Error fetching ledger", error);
      showErrorToast(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClick = (ledgerId) => {
    setLedgerIdToDelete(ledgerId);
    setShowConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      setIsLoading(true);
      const response = await LedgerService.deleteLedger(ledgerIdToDelete);
      if (response && response.statusCode === 200) {
        showSuccessToast(response.message);
        fetchLedgers();
      }
    } catch (error) {
      console.error("Error deleting ledger", error);
      showErrorToast(error.message);
    } finally {
      setIsLoading(false);
      setShowConfirm(false);
      setLedgerIdToDelete(null);
    }
  };

  const handleModalSubmit = async (formData) => {
    try {
      setIsLoading(true);
      let response;
      if (currentLedger) {
        response = await LedgerService.updateLedger(
          currentLedger._id,
          formData
        );
      } else {
        response = await LedgerService.createLedger(formData);
      }
      if (
        response &&
        (response.statusCode === 201 || response.statusCode === 200)
      ) {
        showSuccessToast(response.message);
        fetchLedgers();
        fetchLedgerCalculations();
      }
      setShowModal(false);
    } catch (error) {
      console.error("Error saving ledger", error);
      showErrorToast(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setShowViewModal(false);
    setCurrentLedger(null);
  };

  return (
    <div>
      <LedgerListing
        columns={columns}
        data={filteredData}
        onEdit={handleEdit}
        onDelete={handleDeleteClick}
        handleShow={handleAdd}
        handleFilterChange={handleFilterChange}
        filterText={filterText}
        totalGrant={totalGrant}
        usedGrant={usedGrant}
        remainingGrant={remainingGrant}
      />
      <LedgerModal
        show={showModal}
        handleClose={handleModalClose}
        handleSubmit={handleModalSubmit}
        initialData={currentLedger}
      />
      <LedgerViewModal
        show={showViewModal}
        handleClose={handleModalClose}
        initialData={currentLedger}
      />
      <ConfirmationModal
        show={showConfirm}
        title={locale.deleteConfirmationTitle}
        message={locale.deleteLedgerConfirmationMessage}
        onConfirm={confirmDelete}
        onCancel={() => setShowConfirm(false)}
      />
    </div>
  );
};

export default Ledger;
